import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { craProductService } from '../../services/productService';
import { craPhaseService } from '../../services/phaseService';
import { CraProduct, ESTADO_SOCIEDAD_OPTIONS } from '../../types/InvestmentProduct';
import { CraPhase } from '../../types/Phase';
import CraPhaseForm from './CraPhaseForm';
import ConfirmModal from '../common/ConfirmModal';
import ActionButton from '../common/ActionButton';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import '../users/UserForm.css';
import './PhaseStyles.css';

interface CraFormProps {
  product: CraProduct | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const CraForm: React.FC<CraFormProps> = ({ product, onSuccess, onCancel }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<CraProduct>({
    nombreProducto: '',
    razonSocial: '',
    cifNif: '',
    direccion: '',
    domicilioSocial: '',
    ibanRecaudacion: '',
    bic: '',
    proximaComunicacion: '',
    ticketsObjetivo: undefined,
    valorContableActual: undefined,
    estadoSociedad: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Phases state
  const [phases, setPhases] = useState<CraPhase[]>([]);
  const [phasesLoading, setPhasesLoading] = useState(false);
  const [showPhaseForm, setShowPhaseForm] = useState(false);
  const [editingPhase, setEditingPhase] = useState<CraPhase | null>(null);
  const [phasesExpanded, setPhasesExpanded] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [phaseToDelete, setPhaseToDelete] = useState<CraPhase | null>(null);

  useEffect(() => {
    if (product) {
      setFormData(product);
      loadPhases();
    }
  }, [product]);

  const loadPhases = async () => {
    if (!product?.id) return;
    setPhasesLoading(true);
    try {
      const response = await craPhaseService.getAll(product.id);
      setPhases(response.data);
    } catch (err) {
      console.error('Error loading phases:', err);
    } finally {
      setPhasesLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'ticketsObjetivo'
        ? (value ? parseInt(value) : undefined)
        : name === 'valorContableActual'
        ? (value ? parseFloat(value) : undefined)
        : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (product?.id) {
        await craProductService.update(product.id, formData);
        showToast('Producto CRA actualizado correctamente');
      } else {
        await craProductService.create(formData);
        showToast('Producto CRA creado correctamente');
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar el producto CRA');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPhase = () => {
    setEditingPhase(null);
    setShowPhaseForm(true);
  };

  const handleEditPhase = (phase: CraPhase) => {
    setEditingPhase(phase);
    setShowPhaseForm(true);
  };

  const handleDeletePhase = (phase: CraPhase) => {
    setPhaseToDelete(phase);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!product?.id || !phaseToDelete?.id) return;
    try {
      await craPhaseService.delete(product.id, phaseToDelete.id);
      showToast('Fase eliminada correctamente');
      loadPhases();
    } catch (err) {
      console.error('Error deleting phase:', err);
      showToast('Error al eliminar la fase', 'error');
    } finally {
      setShowDeleteModal(false);
      setPhaseToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPhaseToDelete(null);
  };

  const handlePhaseSuccess = () => {
    setShowPhaseForm(false);
    setEditingPhase(null);
    loadPhases();
  };

  if (showPhaseForm && product?.id) {
    return (
      <CraPhaseForm
        productId={product.id}
        phase={editingPhase}
        onSuccess={handlePhaseSuccess}
        onCancel={() => setShowPhaseForm(false)}
      />
    );
  }

  return (
    <div className="user-form">
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Eliminar Fase"
        message={`¿Estás seguro de que deseas eliminar la fase "${phaseToDelete?.nombreFase}"?`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />
      <h3>{product ? 'Editar Producto CRA' : 'Crear Producto CRA'}</h3>
      <p className="form-subtitle">Participativo - % sobre Beneficio, variable</p>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <h4>Identificación</h4>
        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="nombreProducto">Nombre del Producto *</label>
            <input
              type="text"
              id="nombreProducto"
              name="nombreProducto"
              value={formData.nombreProducto}
              onChange={handleChange}
              required
              maxLength={200}
            />
          </div>
          <div className="form-group span-1">
            <label htmlFor="cifNif">CIF/NIF *</label>
            <input
              type="text"
              id="cifNif"
              name="cifNif"
              value={formData.cifNif}
              onChange={handleChange}
              required
              maxLength={20}
            />
          </div>
          <div className="form-group span-3">
            <label htmlFor="direccion">Dirección Activo</label>
            <input
              type="text"
              id="direccion"
              name="direccion"
              value={formData.direccion || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group span-3">
            <label htmlFor="razonSocial">Razón Social *</label>
            <input
              type="text"
              id="razonSocial"
              name="razonSocial"
              value={formData.razonSocial}
              onChange={handleChange}
              required
              maxLength={200}
            />
          </div>
          <div className="form-group span-3">
            <label htmlFor="ibanRecaudacion">IBAN Recaudación</label>
            <input
              type="text"
              id="ibanRecaudacion"
              name="ibanRecaudacion"
              value={formData.ibanRecaudacion || ''}
              onChange={handleChange}
              maxLength={34}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="bic">BIC / SWIFT</label>
            <input
              type="text"
              id="bic"
              name="bic"
              value={formData.bic || ''}
              onChange={handleChange}
              maxLength={11}
              placeholder="Ej: BSCHESMMXXX"
            />
          </div>
          <div className="form-group span-4">
            <label htmlFor="domicilioSocial">Domicilio Social</label>
            <input
              type="text"
              id="domicilioSocial"
              name="domicilioSocial"
              value={formData.domicilioSocial || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        <h4>Configuración CRA</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="ticketsObjetivo">Tickets Objetivo</label>
            <input
              type="number"
              id="ticketsObjetivo"
              name="ticketsObjetivo"
              value={formData.ticketsObjetivo || ''}
              onChange={handleChange}
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="valorContableActual">Valor Contable Actual</label>
            <input
              type="number"
              id="valorContableActual"
              name="valorContableActual"
              value={formData.valorContableActual || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="estadoSociedad">Estado Sociedad</label>
            <select
              id="estadoSociedad"
              name="estadoSociedad"
              value={formData.estadoSociedad || ''}
              onChange={handleChange}
            >
              <option value="">Seleccionar...</option>
              {ESTADO_SOCIEDAD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="proximaComunicacion">Próxima Comunicación</label>
            <input
              type="date"
              id="proximaComunicacion"
              name="proximaComunicacion"
              value={formData.proximaComunicacion || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        {product && (
          <>
            <h4>Valoración</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Valor Estimado</label>
                <input type="text" value={formatCurrency(formData.valorEstimado)} disabled />
              </div>
              <div className="form-group">
                <label>Plusvalía Estimada</label>
                <input type="text" value={formatCurrency(formData.plusvaliaEstimada)} disabled />
              </div>
              <div className="form-group">
                <label>% Plusvalía</label>
                <input type="text" value={formatPercent(formData.porcentajePlusvalia)} disabled />
              </div>
            </div>

            <h4>Métricas Capital</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Capital Captado</label>
                <input type="text" value={formatCurrency(formData.capitalCaptado)} disabled />
              </div>
              <div className="form-group">
                <label>Capital Devuelto</label>
                <input type="text" value={formatCurrency(formData.capitalDevuelto)} disabled />
              </div>
              <div className="form-group">
                <label>Capital Vivo</label>
                <input type="text" value={formatCurrency(formData.capitalVivo)} disabled />
              </div>
            </div>

            <h4>Métricas Salida</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Importe Estimado Salida</label>
                <input type="text" value={formatCurrency(formData.importeEstimadoSalida)} disabled />
              </div>
            </div>

            {/* Phases Section */}
            <div className="phases-section">
              <div className="phases-header" onClick={() => setPhasesExpanded(!phasesExpanded)}>
                <h4>
                  <span className={`expand-icon ${phasesExpanded ? 'expanded' : ''}`}>&#9654;</span>
                  Fases del Producto ({phases.length})
                </h4>
                <ActionButton
                  icon={FaPlus}
                  text="Añadir Fase"
                  onClick={() => handleAddPhase()}
                  variant="primary"
                />
              </div>
              {phasesExpanded && (
                <div className="phases-content">
                  {phasesLoading ? (
                    <p>Cargando fases...</p>
                  ) : phases.length === 0 ? (
                    <p className="no-phases">No hay fases definidas para este producto.</p>
                  ) : (
                    <table className="phases-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Nombre</th>
                          <th>Cap. Objetivo</th>
                          <th>Total Captado</th>
                          <th>Tickets</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {phases.map((phase) => (
                          <tr key={phase.id}>
                            <td>{phase.id}</td>
                            <td>{phase.nombreFase}</td>
                            <td>{formatCurrency(phase.capObjetivoFase)}</td>
                            <td>{formatCurrency(phase.totalCaptado)}</td>
                            <td>{phase.numTickets || 0}</td>
                            <td className="phase-actions">
                              <ActionButton
                                icon={FaEdit}
                                text="Editar Fase"
                                onClick={() => handleEditPhase(phase)}
                                variant="edit"
                              />
                              <ActionButton
                                icon={FaTrash}
                                text="Eliminar Fase"
                                onClick={() => handleDeletePhase(phase)}
                                variant="delete"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary" disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : product ? 'Actualizar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
};

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatPercent = (value?: number) => {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(2)}%`;
};

export default CraForm;
